/**
 * SK-GRID — the Square-of-Nine harmonic price ladder from a clicked bar.
 *
 * <p>Eight horizontal levels, each the square of the anchor's own root advanced by a quarter:
 *
 * <pre>
 *   P = the clicked bar's CLOSE          S = sqrt(P)
 *   level(d) = (S + d)^2    for d in 0.25, 0.50, 0.75, 1.00, 1.25, 1.50, 1.75, 2.00
 * </pre>
 *
 * Each level is drawn as a horizontal segment from the anchor's own date to one year ahead, so the
 * ladder is a price structure with a stated shelf life rather than a set of levels valid forever.
 *
 * <h2>⚠ EVERY LEVEL IS ABOVE THE CLOSE, and that is the formula rather than an oversight</h2>
 *
 * {@code (S + d)^2 > S^2 = P} for every positive d, so this is a ONE-SIDED ladder. That is worth
 * stating because its sibling is not: {@code sk360} steps the root by ±2 and therefore needs a
 * clamp, since squaring a NEGATIVE root flips the line back upward — a close of 0.25 has a root of
 * 0.5, and {@code (0.5 - 2)^2} is 2.25, nine times the anchor. Nothing here can take that branch,
 * so there is no clamp to get wrong.
 *
 * <h2>⚠ IT REQUIRES A CLICKED BAR, with no fallback</h2>
 *
 * {@code sk360} falls back to the last bar so it is useful on first press. This does not, because
 * the brief specifies the button be DISABLED until a bar is clicked — and a fallback behind a
 * disabled button is a second, invisible answer to "what is this anchored to". If the guard is
 * ever relaxed the fallback has to be added deliberately, which is the point.
 *
 * <h2>⚠ NO PRICE INTERPOLATION WHEN THE AXIS RUNS SHORT</h2>
 *
 * {@code sk360} interpolates the price at a truncated terminus, because its lines are DIAGONAL and
 * cutting one short at its full-length price would show an angle the projection does not have.
 * These are horizontal: a level's price is the same wherever the segment stops, so truncation is
 * purely cosmetic and the price needs no adjustment. The terminus INDEX is reused from
 * {@code sk360Terminus} and its {@code ideal} is deliberately ignored.
 *
 * <p>Reusing that helper is what stops the two studies disagreeing about where "one year ahead"
 * lands on a non-uniform axis — real bars skip exchange holidays while the padded future is pure
 * weekdays, which is the trap that function exists to handle.
 *
 * <h2>The ladder's spread is scale-dependent, and the summary says so</h2>
 *
 * The step is in ROOT units, not percent, so what it is worth in price depends on the anchor. On
 * SPY near 690 the root is about 26 and the ladder spans roughly +2% to +16%; on a $1 instrument
 * the root is 1 and {@code (1 + 2)^2} is 9 — the top rung sits 800% above the anchor. That is
 * inherent to a square-of-nine and not a defect, so the caller reports the span rather than the
 * tool quietly drawing a ladder off the top of the chart.
 */

import { sk360TargetDate, sk360Terminus } from './sk360';
import { pickForTheme, projectionOpacity, PROJECTION_WIDTH } from './overlayInk';

/** Every overlay this tool creates is `skgrid-…`, so one prefix sweep removes the whole figure. */
export const SKGRID_PREFIX = 'skgrid-';

/**
 * The root steps, as specified.
 *
 * <p>Declared rather than generated from a loop so the list IS the specification — a reader
 * checking the brief against the code compares two literals instead of re-deriving a sequence.
 */
export const SKGRID_STEPS = Object.freeze([0.25, 0.50, 0.75, 1.00, 1.25, 1.50, 1.75, 2.00]);

/**
 * Cyan, as a theme pair.
 *
 * <p>⚠ Distinct from SK360's gold BY DESIGN: both tools can be drawn on one chart from the same
 * anchor, and two ladders in one hue would be unreadable exactly where they overlap. Measured
 * against its own canvas: 10.30:1 on dark, 4.34:1 on light, both clear of the 3:1 floor.
 */
export const SKGRID_COLORS = Object.freeze({ dark: '#4DD0E1', light: '#00838F' });

/** Dashed, so the ladder reads as construction geometry rather than as drawn lines. */
export const SKGRID_LINE_TYPE = 'dashed';

/** The colour for the active theme. */
export const skGridColor = (mode) => pickForTheme(SKGRID_COLORS, mode);

/** `$713.20` for a readable price, `$1,204` once the decimals stop carrying information. */
export function skGridMoney(v) {
  if (!Number.isFinite(v)) return '';
  return Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(2);
}

/**
 * The eight levels for a close, nearest first.
 *
 * <p>⚠ A CLOSE OF ZERO OR BELOW RETURNS NULL rather than a ladder at zero. This store holds
 * all-zero rows (HPIL carries 2,089 of them) and a negative close is not a price, so there is no
 * root to step from — the caller says so rather than drawing eight lines on top of each other.
 *
 * @returns {{close:number, root:number, levels:Array<{step:number, price:number}>}|null}
 */
export function skGridLevels(close) {
  const c = Number(close);
  if (!Number.isFinite(c) || c <= 0) return null;
  const root = Math.sqrt(c);
  return {
    close: c,
    root,
    levels: SKGRID_STEPS.map((step) => ({ step, price: (root + step) ** 2 })),
  };
}

/**
 * Builds the eight horizontal overlays.
 *
 * <p>Admin-only, checked here as well as on the button, since a hidden control is a convenience
 * and not a guard — the same rule the recent-pivot projection states.
 *
 * @param {object} barData            `{dates, ohlc}`
 * @param {object} opts
 * @param {boolean} opts.isAdmin
 * @param {number}  opts.anchorIndex  REQUIRED — the last clicked bar
 * @param {string[]} [opts.extendedDates] the padded axis, so a year ahead has a column
 * @returns {{overlays:Array, summary:object}|null}
 */
export function buildSkGrid(barData, {
  isAdmin = false, anchorIndex = null, extendedDates = null,
} = {}) {
  if (!isAdmin) return null;
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return null;

  const { dates, ohlc } = barData;
  if (dates.length === 0) return null;

  // ⚠ No fallback — see the header. An absent anchor is a refusal, not the last bar.
  if (!Number.isInteger(anchorIndex)) return null;
  if (anchorIndex < 0 || anchorIndex >= dates.length) return null;

  const i = anchorIndex;
  // ⚠ [open, CLOSE, low, high] — the tuple order this chart uses. Reading [3] would anchor the
  // whole ladder to the bar's high and look almost right.
  const bar = ohlc[i];
  const grid = skGridLevels(Array.isArray(bar) ? bar[1] : null);
  if (!grid) return null;

  const axis = Array.isArray(extendedDates) && extendedDates.length > dates.length
    ? extendedDates
    : dates;

  const targetDate = sk360TargetDate(dates[i]);
  const terminus = sk360Terminus(axis, dates.length, targetDate);
  if (!terminus || terminus.index <= i) return null;

  const endDate = axis[terminus.index];
  const color = skGridColor();
  const opacity = projectionOpacity();

  const overlays = grid.levels.map(({ step, price }) => ({
    type: 'trendline',
    // No `source`, so it earns no measurement badge: this is construction geometry, not a
    // measured move — the same reason SK360's two lines carry none.
    tool: 'skgrid',
    date1: dates[i],
    price1: price,
    date2: endDate,
    // Horizontal: the same price at both ends, so truncation cannot misstate the level.
    price2: price,
    color,
    lineType: SKGRID_LINE_TYPE,
    lineWidth: PROJECTION_WIDTH,
    lineOpacity: opacity,
    // ⚠ A LABEL, not a symbol. The shared trendline renderer hardcodes `symbol: 'none'`, so a
    // symbol cap would need a change every existing trendline goes through. `endLabel` terminates
    // the line AND names the level, which is the more informative of the two.
    endLabel: `+${step.toFixed(2)}: ${skGridMoney(price)}`,
    // 'left' so a ~90px label cannot land on the price scale — the same reason the Fibonacci
    // levels ask for it.
    endLabelPosition: 'left',
    id: `${SKGRID_PREFIX}${step.toFixed(2)}-${i}`,
  }));

  const top = grid.levels[grid.levels.length - 1].price;
  const bottom = grid.levels[0].price;

  return {
    overlays,
    summary: {
      anchorIndex: i,
      anchorDate: dates[i],
      anchorClose: grid.close,
      root: grid.root,
      levels: grid.levels,
      endDate,
      targetDate,
      truncated: terminus.truncated,
      // How far short the axis fell, in columns, so the caller can say it rather than the line
      // just stopping somewhere.
      shortBy: terminus.truncated ? terminus.ideal - terminus.index : 0,
      // The span the ladder covers, as a percentage of the anchor — see the scale note.
      spanLowPct: ((bottom - grid.close) / grid.close) * 100,
      spanHighPct: ((top - grid.close) / grid.close) * 100,
    },
  };
}

/** Whether any SK-Grid overlay is currently drawn. */
export function hasSkGridOverlays(overlays) {
  return Array.isArray(overlays)
    && overlays.some((o) => typeof o?.id === 'string' && o.id.startsWith(SKGRID_PREFIX));
}

/** Every overlay except this tool's, so the toggle removes the whole ladder and nothing else. */
export function withoutSkGridOverlays(overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays.filter((o) => !(typeof o?.id === 'string' && o.id.startsWith(SKGRID_PREFIX)));
}
